import { clamp } from "lodash";
import { websiteBuilderNormalizeValue } from "../../schema/normalization/websiteBuilderNormalizeValue";
import {
  IWebsiteBuilderContainerBase,
  IWebsiteBuilderContainerDataBase,
} from "./websiteBuilderContainer";
import { CSSProperties } from "react";
import { IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { IWebsiteBuilderSectionEntityStretch, websiteBuilderGenerateDefaultSectionEntityStretch } from "../websiteBuilderSectionEntity";
import { TWebsiteBuilderElement } from "../element/websiteBuilderElement";

export const WEBSITE_BUILDER_FLEX_MIN_GAP = 0;
export const WEBSITE_BUILDER_FLEX_MAX_GAP = 100;

export type TWebsiteBuilderFlexDirection = "row" | "column";

function isWebsiteBuilderFlexDirection(value: string): value is TWebsiteBuilderFlexDirection {
  return value === "row" || value === "column";
}

export type TWebsiteBuilderFlexJustifyContent = "flex-start" | "center" | "flex-end" | "space-between";

function isWebsiteBuilderFlexJustifyContent(value: string): value is TWebsiteBuilderFlexJustifyContent {
  return value === "flex-start" || value === "center" || value === "flex-end" || value === "space-between";
}

export type TWebsiteBuilderFlexAlignItems = "flex-start" | "center" | "flex-end";

function isWebsiteBuilderFlexAlignItems(value: string): value is TWebsiteBuilderFlexAlignItems {
  return value === "flex-start" || value === "center" || value === "flex-end";
}

export interface IWebsiteBuilderFlexSettings {
  direction: TWebsiteBuilderFlexDirection;
  justifyContent: TWebsiteBuilderFlexJustifyContent;
  alignItems: TWebsiteBuilderFlexAlignItems;
  gap: number;
}

interface IWebsiteBuilderFlexData extends IWebsiteBuilderContainerDataBase {
  type: "flex";
  settings: IWebsiteBuilderFlexSettings;
  stretch: IWebsiteBuilderSectionEntityStretch;
}

export interface IWebsiteBuilderFlex extends IWebsiteBuilderContainerBase<TWebsiteBuilderElement[]> {
  container: IWebsiteBuilderFlexData;
}

export function isWebsiteBuilderFlex(node: any): node is IWebsiteBuilderFlex {
  return node?.type === "container" && node.container?.type === "flex";
}

function generateDefaultFlexSettings(): IWebsiteBuilderFlexSettings {
  return {
    direction: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: 16,
  };
}

export function websiteBuilderGenerateDefaultFlex(): IWebsiteBuilderFlex {
  return {
    id: crypto.randomUUID(),
    type: "container",
    container: {
      type: "flex",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 300, height: 300 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 300, height: 300 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 200, height: 200 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultFlexSettings(),
      stretch: websiteBuilderGenerateDefaultSectionEntityStretch(),
    },
    children: [],
  };
}

export function websiteBuilderNormalizeFlexSettings(partialSettings: Partial<IWebsiteBuilderFlexSettings>): IWebsiteBuilderFlexSettings {
  const defaultSettings = generateDefaultFlexSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { direction, justifyContent, alignItems, gap } = partialSettings;

  const normalizedFlexSettings: IWebsiteBuilderFlexSettings = {
    direction: websiteBuilderNormalizeValue({
      type: "string",
      value: direction,
      defaultValue: defaultSettings.direction,
      required: true,
      validate: isWebsiteBuilderFlexDirection,
    }) as TWebsiteBuilderFlexDirection,
    justifyContent: websiteBuilderNormalizeValue({
      type: "string",
      value: justifyContent,
      defaultValue: defaultSettings.justifyContent,
      required: true,
      validate: isWebsiteBuilderFlexJustifyContent,
    }) as TWebsiteBuilderFlexJustifyContent,
    alignItems: websiteBuilderNormalizeValue({
      type: "string",
      value: alignItems,
      defaultValue: defaultSettings.alignItems,
      required: true,
      validate: isWebsiteBuilderFlexAlignItems,
    }) as TWebsiteBuilderFlexAlignItems,
    gap: websiteBuilderNormalizeValue({
      type: "number",
      value: gap,
      defaultValue: defaultSettings.gap,
      required: true,
      transform: (v: number) => clamp(v, WEBSITE_BUILDER_FLEX_MIN_GAP, WEBSITE_BUILDER_FLEX_MAX_GAP),
    }),
  };

  return normalizedFlexSettings;
}

export function websiteBuilderGenerateFlexDesignStyles(
  settings: IWebsiteBuilderFlexSettings,
): IWebsiteBuilderNodeDesignStyles {
  const styleProperties: CSSProperties = {
    flexDirection: settings.direction,
    justifyContent: settings.justifyContent,
    alignItems: settings.alignItems,
    gap: `${settings.gap}px`,
  };

  let styleDeclaration = "";

  styleDeclaration += `flex-direction: ${styleProperties.flexDirection};`;
  styleDeclaration += `justify-content: ${styleProperties.justifyContent};`;
  styleDeclaration += `align-items: ${styleProperties.alignItems};`;
  styleDeclaration += `gap: ${styleProperties.gap};`;

  return {
    className: "website-builder-flex",
    styleProperties,
    styleDeclaration,
  };
}